import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { PlayCircle, Settings, ArrowRight, Check, ChevronsUpDown, FileText, MessageSquare } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import SubscriptionGate from '../components/common/SubscriptionGate';
import { toast } from 'sonner';
import UpgradeModal from '../components/common/UpgradeModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ModuleAccessOverlay from '../components/common/ModuleAccessOverlay';
import { TopicPickerMulti } from '../components/shared/TopicPickerMulti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Nautical grades - surveyors can come from any MMD
const NAUTICAL_GRADES = ['Master', 'Chief Mate', '2MFG'];
const isNauticalGrade = (grade) => NAUTICAL_GRADES.includes(grade);

const PracticeConfigPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { preferences } = usePreferences();
  
  // Module access check
  const { getPracticeSessionsStatus, loading: moduleLoading } = useModuleAccess();
  const practiceStatus = getPracticeSessionsStatus();
  
  // Question type toggle: 'oral' or 'written'
  const [questionType, setQuestionType] = useState('oral');
  
  // Common states
  const [functions, setFunctions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState('');
  const [numQuestions, setNumQuestions] = useState(10);
  const [customQuestionCount, setCustomQuestionCount] = useState('');
  const [topicSelection, setTopicSelection] = useState('all');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [askedFilter, setAskedFilter] = useState('all');
  const [availableQuestions, setAvailableQuestions] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  
  // Oral-specific states
  const [surveyors, setSurveyors] = useState([]);
  const [inactiveSurveyors, setInactiveSurveyors] = useState([]);
  const [surveyorStatus, setSurveyorStatus] = useState('active');
  const [selectedSurveyors, setSelectedSurveyors] = useState([]);
  
  // Written-specific states
  const [writtenFunctions, setWrittenFunctions] = useState([]);
  const [writtenTopics, setWrittenTopics] = useState([]);
  const [dateFromYear, setDateFromYear] = useState('all');
  const [dateFromMonth, setDateFromMonth] = useState('all');
  const [dateToYear, setDateToYear] = useState('all');
  const [dateToMonth, setDateToMonth] = useState('all');
  const [surveyorComboOpen, setSurveyorComboOpen] = useState(false);
  const [surveyorSearchQuery, setSurveyorSearchQuery] = useState('');
  const [writtenAvailableQuestions, setWrittenAvailableQuestions] = useState(0);
  
  // Generate years for dropdown (current year and 10 years back)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => (currentYear - i).toString());
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];

  // Topics available for the currently selected oral function (flat).
  // TopicPickerMulti builds the tree itself using parent_topic_id.
  const oralTopicsForFunction = useMemo(
    () => topics.filter((t) => t.function_id === selectedFunction),
    [topics, selectedFunction]
  );

  // Get filtered surveyors based on status and search
  const getFilteredSurveyors = useMemo(() => {
    let list = [];
    if (surveyorStatus === 'active') {
      list = surveyors;
    } else if (surveyorStatus === 'inactive') {
      list = inactiveSurveyors;
    } else {
      list = [...surveyors, ...inactiveSurveyors];
    }
    
    // Filter by search query
    if (surveyorSearchQuery.trim()) {
      const query = surveyorSearchQuery.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(query));
    }
    
    return list;
  }, [surveyors, inactiveSurveyors, surveyorStatus, surveyorSearchQuery]);

  const checkSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/subscription`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to check subscription:', error);
    }
  }, []);

  const fetchFunctions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      // Filter by grade AND only show oral functions (exclude written)
      const filteredFunctions = response.data.filter(f => 
        f.grade === preferences.grade && 
        f.exam_type !== 'written'
      );
      setFunctions(filteredFunctions);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
      toast.error('Failed to load functions');
    }
  }, [preferences.grade]);

  const fetchWrittenFunctions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      // Filter by grade AND only show written functions
      const filteredFunctions = response.data.filter(f => 
        f.grade === preferences.grade && 
        f.exam_type === 'written'
      );
      setWrittenFunctions(filteredFunctions);
    } catch (error) {
      console.error('Failed to fetch written functions:', error);
    }
  }, [preferences.grade]);

  const fetchTopics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/topics`);
      const filteredTopics = response.data.filter(t => t.grade === preferences.grade);
      setTopics(filteredTopics);
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  }, [preferences.grade]);

  const fetchWrittenTopics = useCallback(async (functionId) => {
    if (!functionId) {
      setWrittenTopics([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/practice/written/topics/${functionId}`);
      setWrittenTopics(response.data.topics || []);
    } catch (error) {
      console.error('Failed to fetch written topics:', error);
      setWrittenTopics([]);
    }
  }, []);

  const fetchSurveyors = useCallback(async () => {
    try {
      // Fetch all surveyors (both active and inactive)
      const response = await axios.get(`${API}/surveyors`);
      
      // For Nautical grades: Show ALL surveyors for the grade (any MMD)
      // For Engineering grades: Filter by Grade + MMD
      const filteredSurveyors = response.data.filter(s => {
        const hasGrade = s.grades.includes(preferences.grade);
        
        // Nautical grades: only filter by grade
        if (isNauticalGrade(preferences.grade)) {
          return hasGrade;
        }
        
        // Engineering grades: filter by grade AND MMD
        // Handle both old format ("Kolkata") and new format ("Kolkata MMD")
        const userMMD = preferences.mmd_location;
        const userMMDNormalized = userMMD?.endsWith(' MMD') ? userMMD : `${userMMD} MMD`;
        const hasMMD = s.mmd_locations.includes(userMMD) || s.mmd_locations.includes(userMMDNormalized);
        
        return hasGrade && hasMMD;
      });
      
      // Separate active and inactive
      const active = filteredSurveyors.filter(s => s.is_active !== false);
      const inactive = filteredSurveyors.filter(s => s.is_active === false);
      
      setSurveyors(active);
      setInactiveSurveyors(inactive);
    } catch (error) {
      console.error('Failed to fetch surveyors:', error);
    }
  }, [preferences.grade, preferences.mmd_location]);

  const checkAvailableQuestions = useCallback(async () => {
    if (!selectedFunction) return;

    try {
      const params = {
        grade: preferences.grade,
        mmd_location: preferences.mmd_location,
        function_id: selectedFunction
      };

      const response = await axios.get(`${API}/user/questions`, { params });
      
      let filteredQuestions = response.data;
      
      // Filter by topics if specific topics selected (support both legacy topic_id and new topic_ids)
      // Iter 105: expand each selected topic to include its descendants
      // so picking a parent transparently matches questions tagged on
      // grandchild+ topics too.
      if (topicSelection === 'specific' && selectedTopics.length > 0) {
        const byParent = new Map();
        topics.forEach((t) => {
          const p = t.parent_topic_id || null;
          if (!byParent.has(p)) byParent.set(p, []);
          byParent.get(p).push(t);
        });
        const expandedTopicIds = new Set(selectedTopics);
        const frontier = [...selectedTopics];
        while (frontier.length) {
          const next = [];
          for (const tid of frontier) {
            for (const child of byParent.get(tid) || []) {
              if (!expandedTopicIds.has(child.id)) {
                expandedTopicIds.add(child.id);
                next.push(child.id);
              }
            }
          }
          frontier.length = 0;
          frontier.push(...next);
        }
        filteredQuestions = filteredQuestions.filter(q => {
          if (q.topic_ids?.length > 0) {
            return q.topic_ids.some(tid => expandedTopicIds.has(tid));
          }
          return expandedTopicIds.has(q.topic_id);
        });
      }
      
      // Filter by surveyors if selected
      if (selectedSurveyors.length > 0) {
        filteredQuestions = filteredQuestions.filter(q => 
          selectedSurveyors.includes(q.surveyor_internal_id) ||
          selectedSurveyors.includes(q.surveyor_external_id)
        );
      }
      
      setAvailableQuestions(filteredQuestions.length);
    } catch (error) {
      console.error('Failed to check available questions:', error);
    }
  }, [selectedFunction, preferences.grade, preferences.mmd_location, topicSelection, selectedTopics, selectedSurveyors]);

  // Check available written questions
  const checkWrittenAvailableQuestions = useCallback(async () => {
    if (!selectedFunction) {
      setWrittenAvailableQuestions(0);
      return;
    }

    try {
      const response = await axios.post(`${API}/practice/written/questions`, null, {
        params: {
          function_id: selectedFunction,
          topics: selectedTopics.join(',') || undefined,
          date_from_year: dateFromYear !== 'all' ? dateFromYear : undefined,
          date_from_month: dateFromMonth !== 'all' ? dateFromMonth : undefined,
          date_to_year: dateToYear !== 'all' ? dateToYear : undefined,
          date_to_month: dateToMonth !== 'all' ? dateToMonth : undefined,
          asked_filter: askedFilter !== 'all' ? askedFilter : undefined,
          num_questions: 1000 // Just to get total count
        }
      });
      setWrittenAvailableQuestions(response.data.total_available || 0);
    } catch (error) {
      console.error('Failed to check written questions:', error);
      setWrittenAvailableQuestions(0);
    }
  }, [selectedFunction, selectedTopics, dateFromYear, dateFromMonth, dateToYear, dateToMonth, askedFilter]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!isAuthenticated()) {
      toast.error('Please login to access Practice Sessions');
      navigate('/');
      return;
    }

    // Skip data fetches when preferences aren't set — the render layer shows
    // a friendly "Set your grade first" card instead of hard-redirecting the
    // user out (which felt jarring and inconsistent with Notebook/Insights).
    if (!preferences.grade || !preferences.mmd_location) {
      return;
    }

    fetchFunctions();
    fetchWrittenFunctions();
    fetchTopics();
    fetchSurveyors();
    checkSubscription();
  }, [authLoading, isAuthenticated, navigate, preferences.grade, preferences.mmd_location, fetchFunctions, fetchWrittenFunctions, fetchTopics, fetchSurveyors, checkSubscription]);

  useEffect(() => {
    if (selectedFunction && questionType === 'oral') {
      checkAvailableQuestions();
    } else if (selectedFunction && questionType === 'written') {
      checkWrittenAvailableQuestions();
      fetchWrittenTopics(selectedFunction);
    }
  }, [selectedFunction, questionType, checkAvailableQuestions, checkWrittenAvailableQuestions, fetchWrittenTopics]);

  // Re-check written questions when filters change
  useEffect(() => {
    if (questionType === 'written' && selectedFunction) {
      checkWrittenAvailableQuestions();
    }
  }, [questionType, selectedFunction, selectedTopics, dateFromYear, dateFromMonth, dateToYear, dateToMonth, askedFilter, checkWrittenAvailableQuestions]);

  // Reset form when switching question type
  useEffect(() => {
    setSelectedFunction('');
    setSelectedTopics([]);
    setTopicSelection('all');
    setAskedFilter('all');
    setNumQuestions(10);
    setCustomQuestionCount('');
    if (questionType === 'written') {
      setDateFromYear('all');
      setDateFromMonth('all');
      setDateToYear('all');
      setDateToMonth('all');
    }
  }, [questionType]);

  const toggleSurveyor = (surveyorId) => {
    setSelectedSurveyors(prev => 
      prev.includes(surveyorId) 
        ? prev.filter(id => id !== surveyorId)
        : [...prev, surveyorId]
    );
  };

  const getEffectiveQuestionCount = () => {
    if (numQuestions === 'custom') {
      return parseInt(customQuestionCount) || 0;
    }
    return numQuestions;
  };

  // Get the actual number of questions to use (auto-adjusted if needed)
  const getAdjustedQuestionCount = () => {
    const maxAvailable = questionType === 'oral' ? availableQuestions : writtenAvailableQuestions;
    const requestedCount = getEffectiveQuestionCount();
    
    if (maxAvailable === 0) return 0;
    if (requestedCount > maxAvailable) return maxAvailable;
    return requestedCount;
  };

  const handleStartPractice = () => {
    if (!selectedFunction) {
      toast.error('Please select a function');
      return;
    }

    const maxAvailable = questionType === 'oral' ? availableQuestions : writtenAvailableQuestions;
    
    if (maxAvailable === 0) {
      toast.error('No questions available with the selected filters');
      return;
    }

    if (questionType === 'oral' && topicSelection === 'specific' && selectedTopics.length === 0) {
      toast.error('Please select at least one topic or choose "All Topics"');
      return;
    }

    // Auto-adjust count if needed
    const adjustedCount = getAdjustedQuestionCount();
    const requestedCount = getEffectiveQuestionCount();
    
    if (adjustedCount < requestedCount) {
      toast.info(`Starting with ${adjustedCount} questions (${maxAvailable} available)`);
    }

    if (questionType === 'written') {
      // Navigate to written practice session
      const config = {
        function_id: selectedFunction,
        num_questions: adjustedCount,
        selected_topics: selectedTopics,
        asked_filter: askedFilter,
        date_from_year: dateFromYear,
        date_from_month: dateFromMonth,
        date_to_year: dateToYear,
        date_to_month: dateToMonth
      };
      navigate('/practice/written/session', { state: { config } });
    } else {
      // Navigate to oral practice session with config
      const config = {
        function_id: selectedFunction,
        num_questions: adjustedCount,
        topic_selection: topicSelection,
        selected_topics: topicSelection === 'specific' ? selectedTopics : [],
        selected_surveyors: selectedSurveyors,
        asked_filter: askedFilter
      };
      navigate('/practice/session', { state: { config } });
    }
  };

  const functionName = questionType === 'oral' 
    ? functions.find(f => f.id === selectedFunction)?.name || ''
    : writtenFunctions.find(f => f.id === selectedFunction)?.name || '';

  // Check if user has access for current grade
  const { canAccessPracticeSessions, loading: subscriptionLoading } = useSubscriptionAccess();
  const subscribedGrade = subscriptionStatus?.subscribed_grade;
  const userRole = subscriptionStatus?.user_role;
  const currentGrade = preferences.grade;
  
  // Admins/sub-admins have access to all grades
  const isAdmin = userRole === 'admin' || userRole === 'sub_admin';
  const hasGradeAccess = isAdmin || subscribedGrade === 'all' || subscribedGrade === currentGrade;
  
  // Access requires: module access from plan AND grade access (admins bypass both)
  const hasAccess = isAdmin || (canAccessPracticeSessions() && hasGradeAccess);

  // Show loading spinner while auth is being verified
  if (authLoading || subscriptionLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  // Module access enforcement - show overlay if practice sessions is not active
  if (!moduleLoading && practiceStatus && practiceStatus !== 'active') {
    return (
      <Layout>
        <ModuleAccessOverlay
          status={practiceStatus}
          featureName="Practice Sessions"
          description={
            practiceStatus === 'coming_soon'
              ? "Practice Sessions are coming soon! We're working hard to bring you this feature. You'll be able to take timed mock tests and assess your preparation."
              : "Practice Sessions are currently not available. Please check back later."
          }
        />
      </Layout>
    );
  }

  // Grade / MMD unset — show the same friendly empty-state card that
  // Notebook + Insights use, instead of the old toast + hard redirect.
  if (isAuthenticated() && (!preferences.grade || !preferences.mmd_location)) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto text-center py-16" data-testid="practice-grade-unset">
          <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Set your grade & MMD first</h2>
          <p className="text-gray-500 mb-6">
            Please set your grade and MMD location in your profile to configure a practice session.
          </p>
          <Button onClick={() => navigate('/profile')} className="bg-[#8B5CF6] hover:bg-[#7C3AED]" data-testid="practice-open-profile-btn">
            Go to Profile
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#8B5CF6]/10 rounded-lg flex-shrink-0">
              <Settings className="w-6 h-6 text-[#8B5CF6]" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Configure Practice Session</h1>
          </div>
          <p className="text-sm sm:text-base text-gray-600">
            Set up your practice test for {preferences.grade} - {preferences.mmd_location}
          </p>
        </div>

        {!hasAccess ? (
          /* Locked State */
          <SubscriptionGate
            hasAccess={false}
            featureName="Practice Sessions"
            moduleKey="practice_sessions"
            subscribedGrade={subscribedGrade}
            targetGrade={preferences.grade}
            description="Access unlimited practice tests with instant results, detailed answer explanations, and track your progress across all functions."
          />
        ) : (
          /* Configuration Form */
          <Card>
          <CardHeader>
            <CardTitle>Test Configuration</CardTitle>
            <CardDescription>
              Choose your preferences for this practice session
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Question Type Toggle */}
            <div>
              <Label className="text-sm font-medium mb-3 block">
                Question Type <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={questionType === 'oral' ? 'default' : 'outline'}
                  className={cn(
                    "flex-1 h-14 flex flex-col items-center justify-center gap-1",
                    questionType === 'oral' ? 'bg-[#8B5CF6] hover:bg-[#8B5CF6]/90' : ''
                  )}
                  onClick={() => setQuestionType('oral')}
                >
                  <MessageSquare className="w-5 h-5" />
                  <span className="text-sm">Oral Questions</span>
                </Button>
                <Button
                  type="button"
                  variant={questionType === 'written' ? 'default' : 'outline'}
                  className={cn(
                    "flex-1 h-14 flex flex-col items-center justify-center gap-1",
                    questionType === 'written' ? 'bg-[#0A7EA4] hover:bg-[#0A7EA4]/90' : ''
                  )}
                  onClick={() => setQuestionType('written')}
                >
                  <FileText className="w-5 h-5" />
                  <span className="text-sm">Written Questions</span>
                </Button>
              </div>
            </div>

            {/* Function Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Function <span className="text-red-500">*</span>
              </Label>
              <Select value={selectedFunction} onValueChange={setSelectedFunction}>
                <SelectTrigger>
                  <SelectValue placeholder="Select function to practice" />
                </SelectTrigger>
                <SelectContent>
                  {(questionType === 'oral' ? functions : writtenFunctions).map((func) => {
                    const suffix = func.function_suffix && func.function_suffix !== 'None' && func.function_suffix !== 'null' ? func.function_suffix : '';
                    return (
                      <SelectItem key={func.id} value={func.id}>
                        F{func.function_number}{suffix} - {func.name}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Written-specific: Date Range Filter */}
            {questionType === 'written' && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                <Label className="text-sm font-medium block">Date Range (Optional)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">From</Label>
                    <div className="flex gap-2">
                      <Select value={dateFromYear} onValueChange={(v) => { setDateFromYear(v); if (v === 'all') setDateFromMonth('all'); }}>
                        <SelectTrigger className="w-24">
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select 
                        value={dateFromMonth} 
                        onValueChange={setDateFromMonth}
                        disabled={dateFromYear === 'all'}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">To</Label>
                    <div className="flex gap-2">
                      <Select value={dateToYear} onValueChange={(v) => { setDateToYear(v); if (v === 'all') setDateToMonth('all'); }}>
                        <SelectTrigger className="w-24">
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select 
                        value={dateToMonth} 
                        onValueChange={setDateToMonth}
                        disabled={dateToYear === 'all'}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          {months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Asked Filter */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Asked Filter
              </Label>
              <Select value={askedFilter} onValueChange={setAskedFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Questions</SelectItem>
                  {questionType === 'oral' && <SelectItem value="once">Asked Once</SelectItem>}
                  <SelectItem value="2+">Asked 2+ Times</SelectItem>
                  <SelectItem value="3+">Asked 3+ Times</SelectItem>
                  <SelectItem value="multiple">Asked Multiple Times</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Filter practice questions by how many times they&apos;ve been asked
              </p>
            </div>

            {/* Topic Selection - Different for oral vs written */}
            {questionType === 'oral' ? (
              <>
                {/* Oral: Topic Selection Type */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Topic Selection</Label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="topicSelection"
                        value="all"
                        checked={topicSelection === 'all'}
                        onChange={(e) => setTopicSelection(e.target.value)}
                        disabled={!selectedFunction}
                        className="w-4 h-4 text-[#8B5CF6]"
                      />
                      <span className="text-sm">All Topics (including questions without topics)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="topicSelection"
                        value="specific"
                        checked={topicSelection === 'specific'}
                        onChange={(e) => setTopicSelection(e.target.value)}
                        disabled={!selectedFunction}
                        className="w-4 h-4 text-[#8B5CF6]"
                      />
                      <span className="text-sm">Select Specific Topics</span>
                    </label>
                  </div>
                </div>

                {/* Oral: Specific Topics Selection - Unified TopicPickerMulti */}
                {topicSelection === 'specific' && selectedFunction && (
                  <div className="pl-6 space-y-2">
                    <Label className="text-sm font-medium mb-2 block">
                      Choose Topics <span className="text-red-500">*</span>
                    </Label>
                    {oralTopicsForFunction.length === 0 ? (
                      <p className="text-sm text-gray-500">No topics available for this function</p>
                    ) : (
                      <TopicPickerMulti
                        topics={oralTopicsForFunction}
                        value={selectedTopics}
                        onChange={setSelectedTopics}
                        placeholder="Select topics…"
                        testIdPrefix="practice-oral-topic"
                        showRedundancyHint={false}
                      />
                    )}
                    {selectedTopics.length > 0 && (
                      <p className="text-xs text-gray-500">{selectedTopics.length} topic(s) selected</p>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Written: Multi-select searchable topics (unified picker) */
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Topics (Optional)
                </Label>
                <TopicPickerMulti
                  topics={writtenTopics}
                  value={selectedTopics}
                  onChange={setSelectedTopics}
                  placeholder="All topics"
                  disabled={!selectedFunction}
                  testIdPrefix="practice-written-topic"
                  showRedundancyHint={false}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to include all topics
                </p>
              </div>
            )}

            {/* Surveyor Selection - Only for Oral - Searchable */}
            {questionType === 'oral' && (
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Filter by Surveyors (Optional)
              </Label>
              <p className="text-xs text-gray-500 mb-2">
                {isNauticalGrade(preferences.grade) 
                  ? 'Nautical exams can have surveyors from any MMD'
                  : 'Leave unselected to include questions from all surveyors'}
              </p>
              
              {/* Surveyor Status Filter */}
              <div className="mb-3">
                <Label className="text-sm font-medium mb-1 block">Surveyor Status</Label>
                <Select value={surveyorStatus} onValueChange={setSurveyorStatus}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="inactive">Past Surveyors Only</SelectItem>
                    <SelectItem value="both">Both Active & Past</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Searchable Surveyor Selector */}
              {surveyors.length === 0 && inactiveSurveyors.length === 0 ? (
                <p className="text-sm text-gray-500">No surveyors available</p>
              ) : (
                <Popover open={surveyorComboOpen} onOpenChange={setSurveyorComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={surveyorComboOpen}
                      className="w-full justify-between h-auto min-h-10 py-2"
                      disabled={!selectedFunction}
                    >
                      <span className="flex flex-wrap gap-1 text-left">
                        {selectedSurveyors.length === 0 ? (
                          <span className="text-gray-500">All surveyors</span>
                        ) : selectedSurveyors.length <= 2 ? (
                          selectedSurveyors.map(surveyorId => {
                            const surveyor = [...surveyors, ...inactiveSurveyors].find(s => s.id === surveyorId);
                            return surveyor ? (
                              <Badge key={surveyorId} variant="secondary" className="text-xs">
                                {surveyor.name}
                              </Badge>
                            ) : null;
                          })
                        ) : (
                          <Badge variant="secondary">{selectedSurveyors.length} surveyors selected</Badge>
                        )}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[320px] p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder="Search surveyors..." 
                        value={surveyorSearchQuery}
                        onValueChange={setSurveyorSearchQuery}
                      />
                      <CommandList className="max-h-[250px]">
                        <CommandEmpty>No surveyors found.</CommandEmpty>
                        <CommandGroup>
                          {getFilteredSurveyors.map((surveyor) => {
                            const isInactive = !surveyor.is_active;
                            return (
                              <CommandItem
                                key={surveyor.id}
                                value={surveyor.name}
                                onSelect={() => toggleSurveyor(surveyor.id)}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedSurveyors.includes(surveyor.id) ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <span className={cn("flex-1", isInactive && "text-gray-500")}>
                                  {surveyor.name}
                                  <span className="text-xs text-gray-400 ml-1">({surveyor.type})</span>
                                  {isInactive && (
                                    <span className="text-xs text-gray-400 ml-1">
                                      {surveyor.inactive_reason === 'transferred' && surveyor.transferred_to_mmd 
                                        ? `→ ${surveyor.transferred_to_mmd}` 
                                        : '(Inactive)'}
                                    </span>
                                  )}
                                </span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
              {selectedSurveyors.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{selectedSurveyors.length} surveyor(s) selected</p>
              )}
            </div>
            )}

            {/* Number of Questions - Placed last so user sees available count after applying filters */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Number of Questions <span className="text-red-500">*</span>
              </Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {[5, 10, 15, 20].map((count) => (
                  <Button
                    key={count}
                    type="button"
                    variant={numQuestions === count ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setNumQuestions(count); setCustomQuestionCount(''); }}
                    disabled={!selectedFunction}
                    className={numQuestions === count ? (questionType === 'oral' ? 'bg-[#8B5CF6]' : 'bg-[#0A7EA4]') : ''}
                  >
                    {count}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant={numQuestions === 'custom' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNumQuestions('custom')}
                  disabled={!selectedFunction}
                  className={numQuestions === 'custom' ? (questionType === 'oral' ? 'bg-[#8B5CF6]' : 'bg-[#0A7EA4]') : ''}
                >
                  Custom
                </Button>
              </div>
              {numQuestions === 'custom' && (
                <Input
                  type="number"
                  min="1"
                  value={customQuestionCount}
                  onChange={(e) => setCustomQuestionCount(e.target.value)}
                  placeholder="Enter number"
                  className="w-32"
                />
              )}
              {selectedFunction && (
                <>
                  <p className={`text-xs mt-2 ${
                    (questionType === 'oral' ? availableQuestions : writtenAvailableQuestions) === 0 
                      ? 'text-red-500 font-medium' 
                      : 'text-gray-500'
                  }`}>
                    {(questionType === 'oral' ? availableQuestions : writtenAvailableQuestions)} questions available
                  </p>
                  {getEffectiveQuestionCount() > (questionType === 'oral' ? availableQuestions : writtenAvailableQuestions) && 
                   (questionType === 'oral' ? availableQuestions : writtenAvailableQuestions) > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠️ Will use all {questionType === 'oral' ? availableQuestions : writtenAvailableQuestions} available questions
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Summary */}
            {selectedFunction && (
              <div className={`p-4 border rounded-lg ${questionType === 'oral' ? 'bg-purple-50 border-purple-200' : 'bg-cyan-50 border-cyan-200'}`}>
                <h4 className={`font-semibold mb-2 ${questionType === 'oral' ? 'text-purple-900' : 'text-cyan-900'}`}>Session Summary</h4>
                <div className={`space-y-1 text-sm ${questionType === 'oral' ? 'text-purple-800' : 'text-cyan-800'}`}>
                  <p>• Type: {questionType === 'oral' ? 'Oral Questions' : 'Written Questions'}</p>
                  <p>• Function: {functionName}</p>
                  <p>• Questions: {getAdjustedQuestionCount()}</p>
                  {questionType === 'oral' ? (
                    <>
                      <p>• Topics: {topicSelection === 'all' ? 'All topics' : `${selectedTopics.length} selected`}</p>
                      <p>• Surveyors: {selectedSurveyors.length === 0 ? 'All' : `${selectedSurveyors.length} selected`}</p>
                    </>
                  ) : (
                    <>
                      <p>• Topics: {selectedTopics.length === 0 ? 'All topics' : `${selectedTopics.length} selected`}</p>
                      {(dateFromYear !== 'all' || dateToYear !== 'all') && (
                        <p>• Date Range: {dateFromYear !== 'all' ? `${dateFromMonth !== 'all' ? dateFromMonth + ' ' : ''}${dateFromYear}` : 'Start'} - {dateToYear !== 'all' ? `${dateToMonth !== 'all' ? dateToMonth + ' ' : ''}${dateToYear}` : 'Now'}</p>
                      )}
                    </>
                  )}
                  <p className="font-semibold mt-2">
                    Starting with: {getAdjustedQuestionCount()} question{getAdjustedQuestionCount() !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            )}

            {/* Start Button */}
            <Button
              onClick={handleStartPractice}
              disabled={!selectedFunction || (questionType === 'oral' ? availableQuestions : writtenAvailableQuestions) === 0}
              className={`w-full text-lg py-6 ${questionType === 'oral' ? 'bg-[#8B5CF6] hover:bg-[#8B5CF6]/90' : 'bg-[#0A7EA4] hover:bg-[#0A7EA4]/90'}`}
            >
              <PlayCircle className="w-5 h-5 mr-2" />
              Start Practice Session
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
        )}
      </div>
      
      <UpgradeModal 
        isOpen={showUpgrade} 
        onClose={() => setShowUpgrade(false)} 
        feature="practice-tests"
        subscribedGrade={subscriptionStatus?.subscribed_grade}
        targetGrade={preferences.grade}
      />
    </Layout>
  );
};

export default PracticeConfigPage;
